import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Login from "../pages/auth/Login";
import PermissionPage from "../pages/permission-page/PermissionPage";
import Home from "../pages/home-page/Home";

const isAuthenticated = () => !!localStorage.getItem("LVaccessToken");

// ✅ Check both Location and Camera permissions
const checkPermissions = async (): Promise<{
  location: boolean;
  camera: boolean;
}> => {
  try {
    const locationResult = await navigator.permissions.query({
      name: "geolocation" as PermissionName,
    });

    const cameraResult = await navigator.permissions.query({
      name: "camera" as PermissionName,
    });

    return {
      location: locationResult.state === "granted",
      camera: cameraResult.state === "granted",
    };
  } catch {
    return { location: false, camera: false };
  }
};

// ✅ PrivateRoute → requires authentication + permissions
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    (async () => {
      const { location, camera } = await checkPermissions();
      const allGranted = location && camera;
      setHasPermission(allGranted);
      setChecked(true);

      // If not granted, go to permissions page
      if (isAuthenticated() && !allGranted) {
        navigate("/permissions", { replace: true });
      }
    })();
  }, [navigate]);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (!checked) return <div>Checking permissions...</div>;
  if (!hasPermission) return null; // Redirect handled above

  return <>{children}</>;
};

// ✅ AuthenticatedOnly → used for permission page (auth required, permission not)
const AuthenticatedOnly: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
};

// ✅ PublicRoute → used for login page (no auth allowed)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return isAuthenticated() ? <Navigate to="/" replace /> : <>{children}</>;
};

// ✅ Main App Routes
const AppRoute: React.FC = () => {
  return (
    <Routes>
      {/* Public route */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Permission route (auth only, no permission check) */}
      <Route
        path="/permissions"
        element={
          <AuthenticatedOnly>
            <PermissionPage />
          </AuthenticatedOnly>
        }
      />

      {/* Home route (requires both permissions + login) */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Home />
          </PrivateRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoute;
